// Ny kundesamhandling: forsiden for ansatte, med de vanligste oppgavene som store knapper.
// Ligger på / og erstattet «Søk / Registrer» (26.10) — se planer/ny-kundesamhandling-plan.md.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { isLeder, erMekaniker } from '../employeeAccess';
import { isAuthenticated } from '../auth';
import { STI, FARGER, synligeHandlinger } from './handlinger';
import Knapp from './Knapp';
import KundeSok from './KundeSok';
import Gjoremal from './Gjoremal';
import Ikon from './Ikon';
import Ansattmeldinger from './Ansattmeldinger';
import { hentMineOrdrer, erGammel } from '../utils/mineOrdrer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Smalt banner i full bredde (handlinger.js, banner: true), f.eks. Sjekk servicekapasitet.
function Banner({ handling }) {
  const farge = FARGER[handling.farge] || FARGER.info;
  return (
    <Link
      to={handling.til}
      className={`group flex min-h-[2.75rem] items-center gap-2 rounded-2xl border-2 px-3 py-1.5 transition active:scale-[0.99] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${farge.flate}`}
    >
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${farge.ikon}`}>
        <Ikon navn={handling.ikon} className="w-5 h-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-bold text-slate-900">{handling.tittel}</span>
        {handling.hjelp && <span className="text-slate-600"><span aria-hidden="true"> · </span><span className="sr-only">: </span>{handling.hjelp}</span>}
      </span>
      <Ikon navn="pil" className="w-5 h-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
    </Link>
  );
}

// Radene med store knapper under Gjøremål (handlinger.js, feltet rad).
const RADER = [
  { id: 'ordre', tittel: 'Ordre' },
  { id: 'service', tittel: 'Service' },
  { id: 'trykk', tittel: 'Trykk' },
];

// Leser valgt ansatt på nytt når NavBar melder bytte.
function useValgtAnsatt() {
  const les = () => ({ navn: Cookies.get('selectedEmployee') || '', leder: isLeder(), mekaniker: erMekaniker(), butikk: Cookies.get('selectedStore') || '' });
  const [ansatt, setAnsatt] = useState(les);
  useEffect(() => {
    const oppdater = () => setAnsatt(les());
    window.addEventListener('employeeUpdated', oppdater);
    window.addEventListener('storeUpdated', oppdater);
    return () => {
      window.removeEventListener('employeeUpdated', oppdater);
      window.removeEventListener('storeUpdated', oppdater);
    };
  }, []);
  return ansatt;
}

function useTellere(leder, navn) {
  const [tellere, setTellere] = useState({});
  useEffect(() => {
    const hent = async () => {
      if (!isAuthenticated()) return;
      const neste = {};
      // Ordrer: den valgte ansattes åpne ordrer, og hvor mange som er eldre enn 7 virkedager.
      const ordrer = navn ? await hentMineOrdrer(navn) : null;
      if (ordrer) neste.ordrer = { antall: ordrer.length, gamle: ordrer.filter(erGammel).length };
      // Ordrevarsler: ordrer med nye meldinger fra kunder (alle ansatte, ikke bare leder).
      try {
        const r = await fetch(`${API_BASE_URL}/orders/varsler?t=${Date.now()}`, { cache: 'no-store' });
        if (r.ok) neste.ordrevarsler = (await r.json()).antall || 0;
      } catch (e) { /* tellerne er bare hjelp */ }
      // Trykkvarsler: trykk med nye meldinger fra kunder (vinnsel 30.10).
      try {
        const r = await fetch(`${API_BASE_URL}/services/varsler?t=${Date.now()}`, { cache: 'no-store' });
        if (r.ok) neste.trykkvarsler = (await r.json()).antall || 0;
      } catch (e) { /* som over */ }
      // Gjøremål teller selv (Gjoremal.jsx), fordi oppgavene kan krysses av på forsiden.
      // Meldinger og nettbutikk er bare for leder, så tellerne deres hentes bare da.
      if (leder) {
        try {
          // X-Ansatt: utviklerne får også varsler om tilbakemeldinger (src/routes/notifications.js).
          const r = await fetch(`${API_BASE_URL}/notifications/unread?t=${Date.now()}`, {
            cache: 'no-store',
            headers: { 'X-Ansatt': encodeURIComponent(Cookies.get('selectedEmployee') || '') },
          });
          if (r.ok) neste.varsling = (await r.json()).totalUnread || 0;
        } catch (e) { /* tellerne er bare hjelp */ }
        try {
          const r = await fetch(`${API_BASE_URL}/nettbutikk-ordre/nye?t=${Date.now()}`, { cache: 'no-store' });
          if (r.ok) neste.nettbutikk = (await r.json()).antall || 0;
        } catch (e) { /* som over */ }
      }
      setTellere(neste);
    };
    hent();
    const id = setInterval(hent, 60000);
    return () => clearInterval(id);
  }, [leder, navn]);
  return tellere;
}

export default function Forside() {
  const navigate = useNavigate();
  const ansatt = useValgtAnsatt();
  const tellere = useTellere(ansatt.leder, ansatt.navn);
  const [sok, setSok] = useState('');

  const synlige = synligeHandlinger(ansatt.leder, ansatt.mekaniker);
  const gruppe = (g) => synlige.filter((h) => h.gruppe === g);
  const fornavn = ansatt.navn.split(' ')[0];

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <header className="space-y-1">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-balance">
          {fornavn ? `Hei ${fornavn}, hva skal du gjøre?` : 'Hva skal du gjøre?'}
        </h1>
        <p className="text-slate-600">
          {ansatt.butikk || 'Ingen butikk valgt'}
          {ansatt.leder && <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800">Leder</span>}
        </p>
      </header>

      <section aria-label="Finn kunde" className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <KundeSok
          id="nk-sok"
          sok={sok}
          setSok={setSok}
          onVelg={(k) => navigate(`/customer-details/${k._id}`)}
          onNyKunde={(forslag) => navigate(`${STI}/ny-kunde`, { state: { sok, forslag } })}
        />
      </section>

      <Ansattmeldinger ansatt={ansatt.navn} butikk={ansatt.butikk} />

      <section aria-labelledby="nk-vanlig" className="space-y-2">
        <h2 id="nk-vanlig" className="sr-only">Vanlige oppgaver</h2>
        {gruppe('hoved').filter((h) => h.fremhevet).map((h) => (
          <Gjoremal key={h.id} handling={h} leder={ansatt.leder} />
        ))}
        {gruppe('hoved').filter((h) => h.banner).map((h) => (
          <Banner key={h.id} handling={h} />
        ))}
        {/* Én linje for ordre og én for service (vinnsel, 28.10). På stor skjerm står hele raden på
            én linje med like brede knapper; på mindre skjermer brytes den. */}
        {RADER.map((rad) => {
          const knapper = gruppe('hoved').filter((h) => !h.fremhevet && h.rad === rad.id);
          if (!knapper.length) return null;
          return (
            <div key={rad.id} className="space-y-2 pt-2">
              <h3 id={`nk-rad-${rad.id}`} className="text-sm font-semibold uppercase tracking-wide text-slate-600">{rad.tittel}</h3>
              <ul aria-labelledby={`nk-rad-${rad.id}`} className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-flow-col lg:auto-cols-fr">
                {knapper.map((h) => (
                  <li key={h.id}><Knapp handling={h} stor teller={tellere[h.teller]} /></li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <section aria-labelledby="nk-mer">
        <h2 id="nk-mer" className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Mer</h2>
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
          {/* En mekaniker som ikke er leder, får lederknappene som gjelder dem (handling.mekaniker) her. */}
          {[...gruppe('mer'), ...(ansatt.leder ? [] : gruppe('leder'))].map((h) => (
            <li key={h.id}><Knapp handling={h} teller={tellere[h.teller]} /></li>
          ))}
        </ul>
      </section>

      {ansatt.leder ? (
        <section aria-labelledby="nk-leder">
          <h2 id="nk-leder" className="mb-3 text-sm font-semibold uppercase tracking-wide text-rose-800">Leder</h2>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
            {gruppe('leder').map((h) => (
              <li key={h.id}><Knapp handling={h} teller={tellere[h.teller]} /></li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="text-sm text-slate-600">Priser, oppsett, rapporter og nettbutikken vises når en leder er valgt øverst.</p>
      )}
    </div>
  );
}
